import { forwardRef, SyntheticEvent } from 'react';
import { useTranslation } from 'next-i18next';
import cn from 'classnames';
import styled from 'styled-components';

import { COLORS } from '@/shared/constants/colors';
import { BaseProps } from '@/shared/types';
import { Icon } from '@/shared/uikit/Icon';
import { Title } from '@/shared/uikit/Title';

import { Button } from '../Button';

type CollapseSection = {
  title: string;
  index: number;
  open: boolean;
  withActionButton?: boolean;
  onAdd?: (e: SyntheticEvent) => void;
  setOpen: (open: boolean) => void;
  setStep?: (step: number) => void;
} & BaseProps;

export const CollapseSection = forwardRef<HTMLDivElement, CollapseSection>(
  ({ title, index, open, withActionButton, onAdd, setOpen, children, className }, ref) => {
    const { t } = useTranslation('common');

    return (
      <Container className={className} ref={ref} data-index={index}>
        <TitleWrapper type="button" onClick={() => setOpen(!open)}>
          <Title size={18} bold>
            {title}
          </Title>
          <TitleRight>
            {withActionButton && (
              <Button
                size="middle"
                icon={<Icon name="plus" size="s20" />}
                type="text"
                style={{ height: 20 }}
                onClick={onAdd}
              >
                {t('addNew')}
              </Button>
            )}
            <StyledIcon className={cn({ open })} name="caret-down" size="s24" />
          </TitleRight>
        </TitleWrapper>
        {open && <ContentWrapper>{children}</ContentWrapper>}
      </Container>
    );
  },
);

CollapseSection.displayName = 'CollapseSection';

const TitleWrapper = styled.button`
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  background-color: transparent;
  border: none;
  padding: 24px;
`;

const StyledIcon = styled(Icon)`
  &&& {
    color: ${COLORS.systemDark200};
    transition: transform var(--ani-speed) ease;
  }

  &.open {
    transform: rotate(180deg);
  }
`;

const ContentWrapper = styled.div`
  padding: 24px;
  padding-top: 0;
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;

  & + & {
    border-top: 1px solid ${COLORS.systemDark100};
  }
`;

const TitleRight = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
`;
